import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { CheckCircle2, XCircle, Mail } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const VerifyInvitationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [step, setStep] = useState('loading'); // loading | form | invalid | success
  const [invitation, setInvitation] = useState(null);
  const [code, setCode] = useState(searchParams.get('code') || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const codeParam = searchParams.get('code');
    if (codeParam) {
      validateCode(codeParam);
    } else {
      setStep('form');
    }
  }, []);

  const validateCode = async (codeValue) => {
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/users/invitation/${codeValue}`
      );
      setInvitation(data);
      setStep('form');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Convite inválido ou expirado.');
      setStep('invalid');
    }
  };

  const handleCheckCode = async () => {
    if (!code) return;
    setStep('loading');
    await validateCode(code);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('As senhas não conferem');
      return;
    }
    if (password.length < 8) {
      setError('A senha deve ter no mínimo 8 caracteres');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/auth/verify-invitation`,
        { code, password },
        { withCredentials: true }
      );
      setStep('success');
      // Refresh auth then redirect
      setTimeout(async () => {
        await checkAuth();
        navigate('/dashboard');
      }, 1500);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Erro ao ativar conta');
      setSubmitting(false);
    }
  };

  if (step === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary/30 p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (step === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary/30 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-3">
              <XCircle className="h-8 w-8 text-destructive" />
              <CardTitle>Convite inválido</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => navigate('/login')} className="w-full">
              Ir para login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary/30 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
              <CardTitle>Conta ativada!</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              Sua conta foi ativada com sucesso. Você será redirecionado...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Form step
  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary/30 p-4">
      <Card className="w-full max-w-md" data-testid="verify-page">
        <CardHeader>
          <CardTitle className="text-3xl font-black tracking-tight">
            Ativar conta
          </CardTitle>
          {invitation ? (
            <CardDescription className="space-y-1 pt-2">
              <div className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4" />
                <strong className="text-foreground">{invitation.email}</strong>
              </div>
              <div className="text-sm">
                Bem-vindo(a), <strong className="text-foreground">{invitation.name}</strong>!
              </div>
              <div className="text-xs pt-2">
                Defina sua senha para acessar o MeAdd como <strong>{invitation.role}</strong>.
              </div>
            </CardDescription>
          ) : (
            <CardDescription>
              Digite o código de 6 dígitos que você recebeu por e-mail.
            </CardDescription>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!invitation && (
              <div className="space-y-2">
                <Label htmlFor="code">Código de verificação</Label>
                <div className="flex gap-2">
                  <Input
                    id="code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    className="font-mono text-center text-lg tracking-widest"
                    required
                    data-testid="code-input"
                  />
                  <Button type="button" onClick={handleCheckCode} disabled={code.length !== 6}>
                    Verificar
                  </Button>
                </div>
              </div>
            )}
            {invitation && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="password">Nova senha *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mínimo 8 caracteres"
                    required
                    minLength={8}
                    data-testid="password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirme a senha *</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    data-testid="confirm-password-input"
                  />
                </div>
                {error && (
                  <div className="text-sm text-destructive" data-testid="verify-error">
                    {error}
                  </div>
                )}
                <Button
                  type="submit"
                  className="w-full"
                  disabled={submitting}
                  data-testid="activate-btn"
                >
                  {submitting ? 'Ativando...' : 'Ativar conta'}
                </Button>
              </>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
